import { getDetailProduct, getSimilarProducts } from '@api/product';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import { Container, Header, Section } from '@components';
import { RichCard } from '@components/cards';
import { CartButton } from '@components/cartbutton/spa';
import { CarouselContainer } from '@components/container/carousel';
import { Footer } from '@components/footer';
import { Star } from '@components/star';
import {
    TImage,
    TInventory,
    TProduct,
    TVariant,
    TVariantAttr,
} from '@components/types';
import {
    getCombinedName,
    inventoryByVariantId,
} from '@components/variant/variantSelector';
import { RatingWidget } from '@components/widget/rating';
import appConfig from '@core/appconfig';
import {
    getFirst,
    getImageObject,
    getSafeUrl,
    getSortedImages,
    humanizeCurrency,
    sanityIoImageLoader,
    trimToLength,
} from '@core/utils';
import { BadgeCheck, Check, ChevronRight, Circle, CircleCheck, ImageOff, Store as StoreIcon } from 'lucide-react';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import 'swiper/css';
import 'swiper/css/pagination';
import { SwiperSlide } from 'swiper/react';

interface Props {
  initialProduct?: TProduct;
  isMobile?: boolean;
}

export const getServerSideProps: GetServerSideProps<{
  initialProduct: TProduct;
}> = async (context) => {
  const { slug } = context.params;
  const UA = context.req.headers['user-agent'];
  const isMobile = Boolean(
    UA.match(
      /Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i
    )
  );
  try {
    const res = getDetailProduct(parseInt(slug[0] as string));
    const initialProduct: TProduct = await res;

    return {
      props: {
        isMobile,
        initialProduct,
      },
    };
  } catch (error) {
    console.error('Error fetching product:', error);
    return {
      props: {
        isMobile,
        initialProduct: null,
      },
    };
  }
};

const AddToCart = dynamic(
  () => import('../../src/components/addtocart').then((mod) => mod.AddToCart),
  {
    ssr: false,
  }
);

const Navbar = dynamic(
  () => import('../../src/components/navbar').then((mod) => mod.Navbar),
  {
    ssr: false,
  }
);

const ProductDetail: React.FC = ({ initialProduct, isMobile }: Props) => {
  const router = useRouter();
  const [mainImage, setMainImage] = useState<TImage>();
  const [storeId, setStoreId] = useState<number>();
  const [product, setProduct] = useState<TProduct>(initialProduct);
  const [similarProducts, setSimilarProducts] = useState<TProduct[]>();
  const [selectedInventory, setselectedInventory] = useState<TInventory>(
    getFirst(initialProduct?.inventories)
  );
  const [variant, setVariant] = useState<TVariant>(
    selectedInventory?.is_available && selectedInventory?.variant
      ? selectedInventory?.variant
      : product?.variants?.filter(
          (variant) =>
            getFirst(inventoryByVariantId(variant, product))?.is_available ==
            true
        )[0]
  );
  let content = null;
  const [productAttrs, setProductAttrs] = useState<TVariantAttr[]>([]);
  const [visibleAttrs, setVisibleAttrs] = useState(6);

  const onClickVariant = (value: TVariant) => {
    setVariant(value);
    setselectedInventory(
      getFirst(inventoryByVariantId(value, product, storeId))
    );
  };

  useEffect(() => {
    const { store_id } = router.query;
    const storeId = store_id && parseInt(store_id as string);
    if (storeId > 0) {
      setStoreId(storeId);
    }
    let id = (
      router.query.slug === undefined ? router.query.slug : router.query.slug[0]
    ) as string;
    if (!router.isReady && id == product?.id.toString()) {
      return;
    }
    getDetailProduct(parseInt(id)).then((product) => {
      setProduct(product);
      if (storeId > 0) {
        setselectedInventory(
          getFirst(
            product.inventories?.filter(
              (inventory) => inventory.store_id == storeId
            )
          )
        );
      } else {
        setselectedInventory(getFirst(product.inventories));
      }
      setMainImage(getImageObject(product?.images));
    });
  }, [router.isReady, router.query.slug]);

  useEffect(() => {
    if (product?.id != undefined) {
      getSimilarProducts(product?.id).then((products) =>
        setSimilarProducts(products)
      );
    }
  }, [product?.category_id, router.query.slug]);

  useEffect(() => {
    if (selectedInventory?.variant?.attrs) {
      setProductAttrs(selectedInventory?.variant?.attrs);
    } else setProductAttrs([]);
    setVariant(
      selectedInventory?.is_available && selectedInventory?.variant
        ? selectedInventory?.variant
        : product?.variants?.find(
            (variant) =>
              getFirst(inventoryByVariantId(variant, product, storeId))
                ?.is_available == true
          )
    );
  }, [selectedInventory?.variant]);

  if (product) {
    content = (
      <Container>
        <Head>
          <title>{`${product?.name} - ${appConfig.appName}`}</title>
          <meta
            name="Keywords"
            content={`Local Products, Local Services,${product?.name},Jhattse`}
          />
          <meta
            name="Description"
            content={`Get ${product?.name} from nearby local stores on ${appConfig.appName}`}
          />
          <link
            rel="canonical"
            href={`https://jhattse.com/product/${product.id}/${getSafeUrl(
              product?.name
            )}`}
          />
          <meta property="og:title" content={`${product?.name} on ${appConfig.appName}`} />
          <meta
            property="og:image"
            content={`${getFirst(product?.images)?.url}`}
          />
          <meta
            name="og:description"
            content={`Get ${product?.name} from nearby local stores on ${appConfig.appName}`}
          />
          <meta
            property="og:url"
            content={`https://jhattse.com/product/${product.id}/${getSafeUrl(
              product?.name
            )}`}
          />
          <link
            href="https://fonts.googleapis.com/css2?family=Montserrat:wght@100..900&display=swap"
            rel="stylesheet"
          ></link>
          {product?.images.length > 0 && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(
                  {
                    '@context': 'https://schema.org',
                    '@type': 'Product',
                    image: (product?.images || []).map((im) => {
                      return im.url;
                    }),
                    name: product?.name,
                    description: product?.description || '',

                    brand: {
                      '@type': 'Brand',
                      name: product?.brand?.name,
                    },
                    aggregateRating: {
                      '@type': 'AggregateRating',
                      ratingValue: product?.stats?.rating_overall,
                      bestRating: '5',
                      reviewCount: product?.stats?.rating_count,
                    },
                    offers: {
                      '@type': 'Offer',
                      price: product?.mrp,
                      priceCurrency: 'INR',
                    },
                    url: `https://jhattse.com/product/${
                      product?.id
                    }/${getSafeUrl(product?.name)}`,
                  },
                  (key, value) => {
                    // Check if key matches the string "pin" or "mob"
                    // if matched return value "undefined"
                    if (key === 'image') {
                      return product?.images?.length > 0 ? value : undefined;
                    }
                    if (key === 'brand') {
                      return product?.brand != undefined &&
                        product?.brand != null
                        ? value
                        : undefined;
                    }
                    if (key === 'aggregateRating') {
                      return product?.stats?.rating_count > 0
                        ? value
                        : undefined;
                    }
                    return value;
                  }
                ),
              }}
            />
          )}
        </Head>
        <Header backText={product?.name} />
        <div className="flex flex-col font-manrope gap-2 w-full mx-auto max-w-[1240px] pb-16 min-h-[70vh] bg-gradient-to-b from-[#EEF2FA] to-neutral-50 md:bg-none">
          <div className="hidden px-2 pt-6 pb-2 md:flex items-center">
            <Breadcrumb className="flex items-center gap-1 text-sm text-neutral-500 font-medium font-manrope list-none">
              <BreadcrumbItem>
                <BreadcrumbLink href="/" className="hover:text-business-500 transition-colors">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <ChevronRight size={14} className="text-neutral-300 mx-1" />
              <BreadcrumbItem>
                <BreadcrumbLink href="/product/trending" className="hover:text-business-500 transition-colors">
                  Product
                </BreadcrumbLink>
              </BreadcrumbItem>
              <ChevronRight size={14} className="text-neutral-300 mx-1" />
              <BreadcrumbItem
                className="text-ellipsis text-left break-words line-clamp-1 select-none text-business-500 font-semibold"
                isCurrentPage
              >
                <BreadcrumbLink
                  href={`/product/${product.id}/${getSafeUrl(product?.name)}`}
                >
                  {trimToLength(product.name, 80)}
                </BreadcrumbLink>
                <ol className="p-0 list-none"></ol>
              </BreadcrumbItem>
            </Breadcrumb>
          </div>
          <div className="px-2 flex md:flex-row md:items-start md:gap-8 gap-3 lt-sm:py-4 flex-col font-manrope">
            <div className="flex md:flex-row flex-col-reverse gap-3 md:sticky lt-sm:relative top-6 flex-shrink-0">
              <div className={`${getSortedImages(product?.images)?.length > 1 ? 'md:flex' : ''} md:flex-col flex-row gap-2 hidden overflow-x-scroll no-scrollbar max-h-80`}>
                <CarouselContainer direction="vertical">
                  {getSortedImages(product?.images)?.map((image) => {
                    return (
                      <SwiperSlide
                        style={{ width: 'auto', height: 'auto' }}
                        className="min-w-0"
                        onClick={(e) => setMainImage(image)}
                      >
                        <div className={`shrink-0 w-14 h-14 rounded-xl overflow-hidden border-2 cursor-pointer transition-colors ${mainImage?.url === image?.url ? 'border-business-500' : 'border-transparent hover:border-neutral-200'}`}>
                          <Image
                            loader={sanityIoImageLoader}
                            width="200"
                            height="200"
                            className="flex items-center justify-center w-full h-full object-cover"
                            src={image?.url}
                            alt={image?.description || product?.name}
                          />
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </CarouselContainer>
              </div>
              <div className="hidden md:flex flex-col gap-4 rounded-2xl overflow-hidden relative bg-white border border-neutral-200/70 shadow-sm">
                {product?.tag != undefined && product?.tag?.length > 0 && (
                  <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-brand-500 w-min font-manrope text-xs font-semibold text-white shadow-sm whitespace-nowrap">
                    {product.tag}
                  </div>
                )}
                <div className="flex items-center justify-center w-80 h-80 lt-sm:w-full bg-neutral-50">
                  {mainImage?.url ? (
                    <Image
                      loader={sanityIoImageLoader}
                      priority={true}
                      src={mainImage.url}
                      alt={mainImage?.description || product.name}
                      width="224"
                      height="224"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-neutral-400">
                      <ImageOff className="w-10 h-10 text-neutral-300" strokeWidth={1.5} />
                      <span className="text-sm font-medium">No image available</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col flex-1 min-w-0 gap-6 font-manrope md:bg-white md:border md:border-solid md:border-neutral-200/70 md:shadow-sm md:rounded-2xl md:p-6">
              <div className="flex flex-col leading-snug gap-2.5">
                {isMobile && (
                  <div className="md:hidden flex w-full rounded-2xl overflow-hidden bg-white border border-neutral-200/70 shadow-sm relative">
                    {product?.tag != undefined && product?.tag?.length > 0 && (
                      <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-brand-500 w-min font-manrope text-xs font-semibold text-white shadow-sm whitespace-nowrap">
                        {product.tag}
                      </div>
                    )}
                    {!(getSortedImages(product?.images)?.length > 0) && (
                      <div className="w-full h-56 bg-neutral-50 flex flex-col items-center justify-center gap-2 text-neutral-400">
                        <ImageOff className="w-10 h-10 text-neutral-300" strokeWidth={1.5} />
                        <span className="text-sm font-medium">No image available</span>
                      </div>
                    )}
                    <CarouselContainer direction="horizontal">
                      {getSortedImages(product?.images)?.map(
                        (image: TImage, index: number) => {
                          return (
                            <SwiperSlide className="w-full h-fit">
                              <div className="h-70 relative bg-neutral-50 flex items-center justify-center">
                                {image?.url ? (
                                  <Image
                                    loader={sanityIoImageLoader}
                                    fill
                                    className="absolute inset-0 aspect-square object-contain"
                                    src={image?.url}
                                    alt={image?.description || product?.name}
                                    priority={index < 1}
                                    loading={index < 1 ? 'eager' : 'lazy'}
                                  />
                                ) : (
                                  <ImageOff className="w-10 h-10 text-neutral-300" strokeWidth={1.5} />
                                )}
                              </div>
                            </SwiperSlide>
                          );
                        }
                      )}
                    </CarouselContainer>
                  </div>
                )}
                <div className="text-ellipsis text-left break-words line-clamp-2 md:line-clamp-none leading-tight md:max-w-2xl max-w-full">
                  <h1 className="m-0 font-manrope text-neutral-900 md:text-2xl text-xl font-semibold leading-snug">
                    {variant !== undefined && variant !== null
                      ? getCombinedName(product, variant?.id)
                      : product?.name}
                  </h1>
                  {(variant?.description || product?.description) && (
                    <p className="m-0 mt-1 text-sm font-manrope text-neutral-500 font-normal leading-relaxed">
                      {variant?.description || product?.description}
                    </p>
                  )}
                </div>
                {product?.brand && (
                  <div className="text-ellipsis text-left break-words line-clamp-2 md:line-clamp-none leading-tight md:max-w-2xl max-w-full">
                    <span className="text-neutral-500 lg:text-base text-sm font-semibold">
                      Brand:
                    </span>
                    &nbsp;
                    <Link
                      href={`/brand/${product?.brand?.id}/${getSafeUrl(
                        product?.brand?.name
                      )}`}
                      prefetch={false}
                    >
                      <span className="text-business-500 lg:text-base text-sm font-semibold hover:underline">
                        {product?.brand?.name}
                      </span>
                    </Link>
                  </div>
                )}
                {product?.stats?.rating_overall > 0 && (
                  <div className="flex flex-row gap-2 items-center text-sm">
                    <Star rating={product?.stats?.rating_overall} />
                  </div>
                )}
                {selectedInventory?.store?.id > 0 ? (
                  <div className="flex gap-2 items-center flex-wrap">
                    <p className="text-neutral-500 font-medium md:text-md text-sm leading-tight flex items-center gap-1">
                      <StoreIcon size={13} className="flex-shrink-0" />
                      Seller:{' '}
                      <Link
                        href={`/store/${
                          selectedInventory?.store?.id
                        }/${getSafeUrl(selectedInventory?.store?.name)}`}
                        prefetch={false}
                      >
                        <span className="font-semibold text-business-500 hover:underline">
                          {selectedInventory?.store?.name}
                        </span>
                      </Link>
                    </p>
                    {selectedInventory?.store?.verified && (
                      <div className="flex gap-1 items-center z-10 w-max rounded-full font-semibold text-white bg-verified py-0.5 pr-2 pl-1.5 select-none text-[11px]">
                        <BadgeCheck size={13} />
                        Trusted Seller
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs font-semibold text-error-500 bg-error-100 w-fit px-2 py-0.5 rounded-full">
                    Unavailable
                  </p>
                )}

                {product?.inventories?.length > 0 ? (
                  <div className="flex flex-row items-center gap-4 md:text-md text-sm">
                    <span className="text-neutral-900 font-bold md:text-3xl text-2xl">
                      {humanizeCurrency(
                        selectedInventory?.price ||
                          product?.inventories?.filter(
                            (inventory) => inventory.variant_id == variant?.id
                          )[0]?.price ||
                          selectedInventory?.mrp
                      )}
                    </span>
                    {selectedInventory?.price !== null &&
                      selectedInventory?.mrp !== selectedInventory?.price && (
                        <span className="line-through text-neutral-400 font-medium md:text-lg text-base">
                          {humanizeCurrency(
                            selectedInventory?.mrp ||
                              product?.inventories?.filter(
                                (inventory) =>
                                  inventory.variant_id == variant?.id
                              )[0]?.mrp
                          )}
                        </span>
                      )}
                    {selectedInventory?.price != null &&
                    selectedInventory?.price < product.mrp ? (
                      <div className="text-success-500 font-semibold text-sm bg-success-100 px-2 py-0.5 rounded-full">
                        {Math.trunc(
                          ((product.mrp - selectedInventory.price) /
                            product.mrp) *
                            100
                        )}
                        % off
                      </div>
                    ) : (
                      <div></div>
                    )}
                  </div>
                ) : (
                  <></>
                )}

                {selectedInventory?.store?.id > 0 ? (
                  <div className="flex flex-row gap-4 items-center align-bottom md:w-44 w-[calc(100%-8px)] py-2">
                    <CartButton
                      mode={''}
                      Isvariant={false}
                      product={product}
                      variantId={variant?.id}
                      inventory={selectedInventory}
                    ></CartButton>
                  </div>
                ) : (
                  <></>
                )}

                {/* Bottom Section Start */}
                <div className="flex flex-col w-full gap-8">
                  {product.inventories?.length > 0 &&
                  product?.variants?.length > 1 ? (
                    <div>
                      <h2 className="m-0 font-semibold text-base text-neutral-900 pb-2">
                        Variants{' '}
                        <span className="font-medium text-neutral-500">
                          {product?.variants?.length > 1
                            ? `(${product?.variants?.length})`
                            : ``}
                        </span>
                      </h2>
                      <CarouselContainer>
                        {product?.variants?.length > 0 &&
                          product?.variants?.map((item, index: number) => {
                            const isAvailable = inventoryByVariantId(item, product, storeId)[0]?.is_available;
                            const isSelected = item?.id == variant?.id;
                            return (
                              <SwiperSlide
                                style={{ width: 'auto' }}
                                className="min-w-0"
                                key={index}
                              >
                                <div
                                  className={`rounded-xl cursor-pointer lt-sm:w-full border-2 transition-colors px-4 py-3 min-w-32 ${
                                    !isAvailable
                                      ? 'bg-neutral-50 border-neutral-200 opacity-60 cursor-not-allowed'
                                      : isSelected
                                        ? 'bg-business-500/5 border-business-500'
                                        : 'bg-white border-neutral-200 hover:border-business-500/40'
                                  }`}
                                  onClick={() => isAvailable && onClickVariant(item)}
                                >
                                  <div className="flex flex-col gap-1.5">
                                    <div className="flex items-center gap-2">
                                      {isSelected && isAvailable && (
                                        <span className="flex items-center justify-center w-4 h-4 rounded-full bg-business-500 flex-shrink-0">
                                          <Check size={10} className="text-white" strokeWidth={3} />
                                        </span>
                                      )}
                                      <h4 className="text-sm font-semibold text-neutral-800 line-clamp-1">
                                        {trimToLength(
                                          item?.name || product?.name,
                                          20
                                        )}
                                      </h4>
                                    </div>
                                    <span className="text-base font-semibold text-neutral-900">
                                      {humanizeCurrency(
                                        product?.inventories?.filter(
                                          (inventory) =>
                                            inventory.variant_id == item.id
                                        )[0]?.price ||
                                          product?.inventories?.filter(
                                            (inventory) =>
                                              inventory.variant_id == item.id
                                          )[0]?.mrp
                                      ) || 'Unavailable'}
                                    </span>
                                    <p
                                      className={`font-semibold text-xs ${isAvailable ? 'text-success-500' : 'text-error-400'}`}
                                    >
                                      {isAvailable ? 'In Stock' : 'Out of stock'}
                                    </p>
                                  </div>
                                </div>
                              </SwiperSlide>
                            );
                          })}
                      </CarouselContainer>
                    </div>
                  ) : (
                    <></>
                  )}
                  {storeId == undefined && product.inventories?.length > 0 ? (
                    <div className="lt-sm:hidden">
                      <h3 className="m-0 font-semibold text-base text-neutral-900 pb-2">
                        Offers and Pricing
                      </h3>
                      <CarouselContainer>
                        {(
                          product.inventories &&
                          inventoryByVariantId(variant, product, storeId)
                        ).map((inventory: TInventory, index: number) => {
                          const isSelected = selectedInventory?.id == inventory.id;
                          return (
                            <SwiperSlide
                              style={{ width: 'auto' }}
                              className="min-w-0"
                              key={index}
                            >
                              <div
                                key={inventory.id}
                                className={`p-3 rounded-xl cursor-pointer md:w-60 w-full border-2 transition-colors ${
                                  isSelected
                                    ? 'bg-business-500/5 border-business-500'
                                    : 'bg-white border-neutral-200 hover:border-business-500/40'
                                }`}
                                onClick={() => {
                                  setselectedInventory(inventory);
                                }}
                              >
                                <div className="flex flex-col gap-2">
                                  <div className="flex justify-start font-semibold text-sm text-neutral-800 gap-2 line-clamp-1">
                                    {inventory.store.name}
                                  </div>
                                  {inventory.store?.stats?.rating_overall ? (
                                    <Star
                                      rating={
                                        inventory.store?.stats?.rating_overall
                                      }
                                    />
                                  ) : (
                                    <div className="text-neutral-400 text-xs">
                                      No Reviews
                                    </div>
                                  )}
                                  <div className="flex flex-row justify-between items-center pt-1">
                                    <div className="text-base font-semibold text-neutral-900">
                                      {humanizeCurrency(
                                        inventory?.price ||
                                          inventory?.mrp ||
                                          product?.mrp
                                      )}
                                    </div>
                                    {isSelected && (
                                      <span className="text-business-500">
                                        <CircleCheck size={18} />
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </SwiperSlide>
                          );
                        })}
                      </CarouselContainer>
                    </div>
                  ) : (
                    <></>
                  )}

                  {productAttrs.length > 0 && (
                    <div>
                      <h6 className="m-0 text-base text-neutral-900 font-semibold pb-2">
                        Product Information
                      </h6>
                      <div className="grid md:grid-cols-4 grid-cols-3 rounded-xl overflow-hidden border border-neutral-200/70 divide-y divide-neutral-100">
                        {productAttrs
                          .slice(0, visibleAttrs)
                          .map((attribute) => (
                            <>
                              <div className="text-sm text-neutral-600 font-semibold bg-neutral-50 p-3">
                                {attribute?.attr?.name}
                              </div>
                              <div className="col-span-2 md:col-span-3 text-sm text-neutral-800 bg-white font-medium p-3">
                                {attribute?.value}
                              </div>
                            </>
                          ))}
                      </div>
                      {productAttrs.length > visibleAttrs && (
                        <button
                          type="button"
                          onClick={() => setVisibleAttrs(productAttrs.length)}
                          className="text-sm text-business-500 font-manrope font-semibold hover:underline mt-2 cursor-pointer"
                        >
                          View More
                        </button>
                      )}
                    </div>
                  )}

                  {/* For Mobile Device Start */}
                  {storeId == undefined &&
                  inventoryByVariantId(variant, product, storeId)?.length >
                    0 ? (
                    <div className="md:hidden flex flex-col gap-2">
                      <h3 className="m-0 font-semibold text-base text-neutral-900 pb-1">
                        Offers and Pricing
                      </h3>
                      {(
                        product.inventories &&
                        inventoryByVariantId(variant, product, storeId)
                      ).map((inventory: TInventory) => {
                        const isSelected = selectedInventory?.id == inventory.id;
                        return (
                          <button
                            type="button"
                            key={inventory.id}
                            onClick={() => {
                              setselectedInventory(inventory);
                            }}
                            className={`flex items-center gap-2 w-full text-left rounded-xl border-2 p-3 transition-colors cursor-pointer ${
                              isSelected
                                ? 'bg-business-500/5 border-business-500'
                                : 'bg-white border-neutral-200'
                            }`}
                          >
                            {isSelected ? (
                              <CircleCheck size={18} className="text-business-500 flex-shrink-0" />
                            ) : (
                              <Circle size={18} className="text-neutral-300 flex-shrink-0" />
                            )}
                            <div className="flex flex-row gap-2 items-center justify-between flex-1 min-w-0">
                              <div className="flex flex-col items-start gap-1 text-xs font-semibold min-w-0">
                                <span className="text-sm text-neutral-800 line-clamp-1">{inventory.store.name}</span>
                                {product?.stats?.rating_overall ? (
                                  <Star
                                    rating={product?.stats?.rating_overall}
                                  />
                                ) : (
                                  <div className="text-neutral-400 text-xs font-medium">
                                    No Reviews
                                  </div>
                                )}
                              </div>
                              <div className="text-sm font-semibold text-neutral-900 flex-shrink-0">
                                {humanizeCurrency(
                                  inventory?.price ||
                                    inventory?.mrp ||
                                    product?.mrp
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <></>
                  )}
                  {product?.stats?.rating_count > 0 && (
                    <RatingWidget product={product}></RatingWidget>
                  )}
                </div>
                {/* Bottom Section End */}
              </div>
            </div>
          </div>
          {/* For Mobile Device End */}
          <Section
            title="Similar Products"
            products={similarProducts}
            element={RichCard}
            isCarousel={true}
            style="pb-2 text-netural-800 px-4"
            headStyle="md:text-lg text-base py-1 font-semibold"
          />
        </div>
      </Container>
    );
  } else {
    content = (
      <div>
        <div className="w-full">
          <div className="h-12 bg-neutral-200 full dark:bg-neutral-700 max-w-[480px] mb-2.5"></div>
        </div>
        <div className="h-2"></div>
        <div className="space-y-8 m-5 animate-pulse md:space-y-0 md:space-x-8 md:flex md:items-center">
          <div className="flex justify-center items-center max-w-96 h-48 bg-neutral-300 rounded dark:bg-neutral-700">
            <svg
              className="w-12 h-12 text-neutral-200"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 640 512"
            >
              <path d="M480 80C480 35.82 515.8 0 560 0C604.2 0 640 35.82 640 80C640 124.2 604.2 160 560 160C515.8 160 480 124.2 480 80zM0 456.1C0 445.6 2.964 435.3 8.551 426.4L225.3 81.01C231.9 70.42 243.5 64 256 64C268.5 64 280.1 70.42 286.8 81.01L412.7 281.7L460.9 202.7C464.1 196.1 472.2 192 480 192C487.8 192 495 196.1 499.1 202.7L631.1 419.1C636.9 428.6 640 439.7 640 450.9C640 484.6 612.6 512 578.9 512H55.91C25.03 512 .0006 486.1 .0006 456.1L0 456.1z" />
            </svg>
          </div>
          <div className="w-full">
            <div className="h-2.5 bg-neutral-200 rounded-full dark:bg-neutral-700 w-48 mb-4 "></div>
            <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[480px] mb-2.5"></div>
            <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 mb-2.5"></div>
            <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[440px] mb-2.5"></div>
            <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[460px] mb-2.5"></div>
            <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[360px]"></div>
            <div className="h-2"></div>
            <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[460px] mb-2.5"></div>
            <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[460px] mb-2.5"></div>
            <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[460px] mb-2.5"></div>
          </div>
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {content}
      <Navbar />
      <Footer />
    </div>
  );
};

export default ProductDetail;
